import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiPhone, FiGlobe, FiMapPin, FiStar, FiClock, FiNavigation, FiChevronDown, FiChevronUp, FiExternalLink } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

/**
 * CourseDetailsCard - Displays golf course contact and location information
 *
 * @param {Object} course - Golf club data from golf_clubs table
 * @param {boolean} compact - If true, shows only quick-action icons (for Rounds page)
 * @param {boolean} showHours - If true, shows operating hours section
 * @param {boolean} wideLayout - If true, shows 2-column layout with full week hours (no dropdown)
 */
const CourseDetailsCard = ({ course, compact = false, showHours = true, wideLayout = false }) => {
  const [hoursExpanded, setHoursExpanded] = useState(false);

  // Early return if no course data
  if (!course) return null;

  /**
   * Build a multi-line address from the structured fields.
   * Falls back to Google's pre-formatted address when components aren't available.
   */
  const formatAddress = () => {
    if (course.short_formatted_address && !course.city && !course.state) {
      return course.short_formatted_address;
    }
    const parts = [course.address, course.city, course.state, course.country_code]
      .map((p) => (typeof p === 'string' ? p.trim() : ''))
      .filter(Boolean);
    if (parts.length === 0) {
      return course.short_formatted_address || course.address || '';
    }
    return parts.join(', ');
  };

  /**
   * Get today's operating hours from weekdayDescriptions array
   * API format: Monday = index 0, Sunday = index 6
   * JavaScript: Sunday = 0, Monday = 1, etc.
   */
  const getTodayHours = () => {
    if (!course.regular_opening_hours?.weekdayDescriptions) return null;

    const today = new Date().getDay(); // 0 = Sunday
    const dayIndex = today === 0 ? 6 : today - 1; // Convert to API format

    const todayStr = course.regular_opening_hours.weekdayDescriptions[dayIndex];
    if (!todayStr) return null;

    // Parse "Monday: 7:00 AM - 8:00 PM" -> "7:00 AM - 8:00 PM"
    const colonIndex = todayStr.indexOf(': ');
    return colonIndex > -1 ? todayStr.substring(colonIndex + 2) : todayStr;
  };

  /**
   * Get current day index in API format (0 = Monday)
   */
  const getTodayIndex = () => {
    const today = new Date().getDay();
    return today === 0 ? 6 : today - 1;
  };

  const formattedAddress = formatAddress();
  const todayHours = getTodayHours();
  const todayIndex = getTodayIndex();

  // Compact mode - just quick-action icons for Rounds page
  if (compact) {
    const hasAnyAction = course.phone || course.website || course.google_maps_uri;
    if (!hasAnyAction) return null;

    return (
      <div className="flex items-center gap-2">
        {course.phone && (
          <a
            href={`tel:${course.phone}`}
            onClick={(e) => e.stopPropagation()}
            className="w-8 h-8 bg-accent/10 rounded-lg flex items-center justify-center hover:bg-accent transition-colors"
            aria-label="Call course"
          >
            <SafeIcon icon={FiPhone} className="w-4 h-4 text-accent" />
          </a>
        )}
        {course.website && (
          <a
            href={course.website}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="w-8 h-8 bg-accent/10 rounded-lg flex items-center justify-center hover:bg-accent transition-colors"
            aria-label="Visit website"
          >
            <SafeIcon icon={FiGlobe} className="w-4 h-4 text-accent" />
          </a>
        )}
        {course.google_maps_uri && (
          <a
            href={course.google_maps_uri}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="w-8 h-8 bg-accent/10 rounded-lg flex items-center justify-center hover:bg-accent transition-colors"
            aria-label="Get directions"
          >
            <SafeIcon icon={FiNavigation} className="w-4 h-4 text-accent" />
          </a>
        )}
      </div>
    );
  }

  // Wide layout mode - 2-column layout for desktop results page
  if (wideLayout) {
    return (
      <div className="flex gap-6">
        {/* Left column: Contact details */}
        <div className="flex-1 space-y-3">
          {/* Phone */}
          {course.phone && (
            <a
              href={`tel:${course.phone}`}
              className="flex items-center text-text-secondary hover:text-accent transition-colors group/link"
            >
              <SafeIcon icon={FiPhone} className="w-4 h-4 text-text-muted mr-2 group-hover/link:text-accent transition-colors" />
              <span className="text-sm font-medium">{course.phone}</span>
            </a>
          )}

          {/* Website */}
          {course.website && (
            <a
              href={course.website}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center text-text-secondary hover:text-accent transition-colors group/link"
            >
              <SafeIcon icon={FiGlobe} className="w-4 h-4 text-text-muted mr-2 group-hover/link:text-accent transition-colors" />
              <span className="text-sm font-medium">Website</span>
              <SafeIcon icon={FiExternalLink} className="w-3 h-3 ml-1 text-text-muted group-hover/link:text-accent transition-colors" />
            </a>
          )}

          {/* Address */}
          {formattedAddress && (
            <div className="flex items-start">
              <SafeIcon icon={FiMapPin} className="w-4 h-4 text-text-muted mr-2 mt-0.5 flex-shrink-0" />
              <p className="text-sm font-medium text-text-secondary leading-tight">{formattedAddress}</p>
            </div>
          )}

        </div>
      </div>
    );
  }

  // Full mode - detailed course information
  return (
    <div className="space-y-3">
      {/* Phone */}
      {course.phone && (
        <a
          href={`tel:${course.phone}`}
          className="flex items-center text-text-secondary hover:text-accent transition-colors group/link"
        >
          <SafeIcon icon={FiPhone} className="w-5 h-5 text-text-muted mr-3 group-hover/link:text-accent transition-colors" />
          <span className="font-medium">{course.phone}</span>
        </a>
      )}

      {/* Website */}
      {course.website && (
        <a
          href={course.website}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center text-text-secondary hover:text-accent transition-colors group/link"
        >
          <SafeIcon icon={FiGlobe} className="w-5 h-5 text-text-muted mr-3 group-hover/link:text-accent transition-colors" />
          <span className="font-medium">Visit Website</span>
          <SafeIcon icon={FiExternalLink} className="w-4 h-4 ml-2 text-text-muted group-hover/link:text-accent transition-colors" />
        </a>
      )}

      {/* Address */}
      {formattedAddress && (
        <div className="flex items-start">
          <SafeIcon icon={FiMapPin} className="w-5 h-5 text-text-muted mr-3 mt-0.5 flex-shrink-0" />
          <p className="font-medium text-text-secondary">{formattedAddress}</p>
        </div>
      )}

      {/* Rating and opening hours removed from card display - data still stored in database */}
    </div>
  );
};

export default CourseDetailsCard;
